import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, Coins } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { streamChatMessage } from '@/lib/streamChat';
import type { StreamError, StreamResult } from '@/lib/streamChat';
import { useAiConversation } from '@/hooks/queries/useAi';
import {
  PRODUCT_MUTATING_TOOLS,
  useMessageFeedback,
  useSendChatMessage,
} from '@/hooks/mutations/useAiMutations';
import { useIsMobile } from '@/hooks/useIsMobile';
import { AI_CREDITS_STATUS_QUERY_KEY, useAiCreditsStatus } from '@/hooks/queries/useAiCredits';
import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import { useSpeechSynthesis } from '@/hooks/useSpeechSynthesis';
import { ChatMessage } from './ChatMessage';
import { ChatMessageBubble } from './ChatMessageBubble';
import { ChatGlassHeader } from './ChatGlassHeader';
import { ChatInput } from './ChatInput';
import { ChatInlineRecharge } from './ChatInlineRecharge';
import { ChatPromptChips } from './ChatPromptChips';
import { ChatQuotaBanner } from './ChatQuotaBanner';
import { VoiceConversationToggle } from './VoiceConversationToggle';
import { AiCreditsPurchaseSheet } from './AiCreditsPurchaseSheet';
import { QUICK_COMMANDS } from './chatConstants';
import type { AiMessage, ChatUiMessage } from '@/types/ai';

import type { MobileChatVariant } from './ChatMessageBubble';

interface ChatThreadProps {
  conversationId: string;
  onBack: () => void;
  showBackButton?: boolean;
  queuedPrompt?: string | null;
  onQueuedPromptConsumed?: () => void;
  onNewConversation?: () => void;
  /** Masque le header interne (ex: ChatGlassHeader fourni par ChatMobileLayout) */
  hideInternalHeader?: boolean;
  /** Messages initiaux à afficher immédiatement (ex: après création de conversation) */
  initialMessages?: AiMessage[] | null;
  /** Callback quand les messages initiaux ont été consommés par le query server */
  onInitialMessagesConsumed?: () => void;
  /** Queue de prompts à envoyer en séquence (routines) */
  routineQueue?: string[] | null;
  /** Callback quand la routine est terminée */
  onRoutineComplete?: () => void;
  /** Mobile chat style variant */
  mobileVariant?: MobileChatVariant;
}

/** Extract a short summary for TTS: first 2 sentences or 200 chars, whichever is shorter. */
function extractTtsSummary(text: string): string {
  // Split on sentence-ending punctuation followed by a space or newline.
  const sentences = text.split(/(?<=[.!?])\s+/);
  const summary = sentences.slice(0, 2).join(' ');
  return summary.length > 200 ? `${summary.slice(0, 197)}...` : summary;
}

function toUiMessage(message: AiMessage): ChatUiMessage {
  return {
    ...message,
    status: 'done',
  };
}

function makeId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.round(Math.random() * 10000)}`;
}

// Session-level: once the backend reports STREAMING_UNAVAILABLE (AI SDK off),
// skip the streaming attempt and go straight to the request/response path.
let streamingUnavailable = false;

function normalizeErrorMessage(error: unknown, t: (key: string) => string) {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'RATE_LIMIT_EXCEEDED':
        return t('chat_rate_limit');
      case 'INSUFFICIENT_CREDITS':
      case 'FEATURE_NOT_AVAILABLE':
        return "Crédits IA insuffisants. Achetez des crédits pour continuer à utiliser l'assistant.";
      case 'NO_SHOP':
        return 'Aucune boutique sélectionnée. Veuillez sélectionner une boutique et réessayer.';
      case 'AI_RATE_LIMITED':
        return "L'assistant est temporairement surchargé. Patientez quelques secondes puis réessayez.";
      case 'AI_OVERLOADED':
        return 'Le service IA est momentanément surchargé. Réessayez dans une minute.';
      case 'AI_API_ERROR':
        return "L'assistant IA est temporairement indisponible. Veuillez réessayer dans quelques instants.";
      case 'VALIDATION_ERROR':
        return 'Message invalide. Vérifiez votre saisie et réessayez.';
      case 'STREAMING_UNAVAILABLE':
        return "Le streaming n'est pas disponible actuellement.";
      default:
        // Use the API error message if it's user-facing, otherwise fallback
        if (error.message && error.message.length < 200) {
          return error.message;
        }
        return t('chat_error');
    }
  }
  if (error instanceof Error) {
    if (error.message === 'Failed to fetch' || error.message.includes('NetworkError')) {
      return 'Connexion impossible. Vérifiez votre connexion internet et réessayez.';
    }
    if (error.message.includes('timeout') || error.message.includes('Timeout')) {
      return 'La requête a pris trop de temps. Veuillez réessayer.';
    }
    return error.message;
  }
  return t('chat_error');
}

export function ChatThread({
  conversationId,
  onBack,
  showBackButton = true,
  queuedPrompt = null,
  onQueuedPromptConsumed,
  onNewConversation,
  hideInternalHeader = false,
  initialMessages = null,
  onInitialMessagesConsumed,
  routineQueue = null,
  onRoutineComplete,
  mobileVariant = 'default',
}: ChatThreadProps) {
  const { t } = useTranslation('dashboard');
  const isMobile = useIsMobile();
  const { data, isLoading, error } = useAiConversation(conversationId);
  const queryClient = useQueryClient();
  const sendMessage = useSendChatMessage(conversationId);
  const feedbackMutation = useMessageFeedback(conversationId);
  const { data: creditsStatus } = useAiCreditsStatus();
  const [localMessages, setLocalMessages] = useState<ChatUiMessage[]>([]);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [threadErrorCode, setThreadErrorCode] = useState<string | null>(null);
  const [pendingPairId, setPendingPairId] = useState<string | null>(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [pendingRoutineSteps, setPendingRoutineSteps] = useState<string[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const streamTimersRef = useRef<Map<string, number>>(new Map());

  // ── Voice conversation mode (hooks declared early, logic wired after timeline) ──
  const [voiceConversation, setVoiceConversation] = useState(false);
  const voiceConversationRef = useRef(false);
  const lastSpokenMsgIdRef = useRef<string | null>(null);
  const pendingSendRef = useRef<string | null>(null);

  const { speak, stop: stopSpeaking, isSpeaking, isSupported: ttsSupported } = useSpeechSynthesis();
  const {
    isSupported: sttSupported,
    isListening,
    startListening,
    stopListening,
  } = useSpeechRecognition({
    continuous: true,
    onResult: (text, isFinal) => {
      if (isFinal && text.trim() && voiceConversationRef.current) {
        // Store for deferred send (handleSend is declared later).
        pendingSendRef.current = text.trim();
      }
    },
  });

  const voiceSupported = sttSupported && ttsSupported;

  // Keep ref in sync for use inside callbacks.
  useEffect(() => {
    voiceConversationRef.current = voiceConversation;
    if (!voiceConversation) {
      stopListening();
      stopSpeaking();
    }
  }, [voiceConversation, stopListening, stopSpeaking]);

  function handleVoiceToggle(enabled: boolean) {
    setVoiceConversation(enabled);
    if (enabled) {
      startListening();
    }
  }

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollButton(distanceFromBottom > 200);
  }, []);

  useEffect(() => {
    const timers = streamTimersRef.current;
    return () => {
      timers.forEach((timer) => window.clearInterval(timer));
      timers.clear();
    };
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.messages, localMessages]);

  const queuedPromptConsumedRef = useRef(false);
  useEffect(() => {
    if (!queuedPrompt || queuedPromptConsumedRef.current || sendMessage.isPending) return;
    queuedPromptConsumedRef.current = true;
    void handleSend(queuedPrompt);
    onQueuedPromptConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuedPrompt, sendMessage.isPending]);

  // ── Routine queue: initialize from prop ──────────────────────────────────────
  const routineInitRef = useRef(false);
  useEffect(() => {
    if (routineQueue && routineQueue.length > 0 && !routineInitRef.current) {
      routineInitRef.current = true;
      setPendingRoutineSteps(routineQueue);
    }
  }, [routineQueue]);

  // ── Routine queue: auto-send next step when previous completes ─────────────
  useEffect(() => {
    if (pendingRoutineSteps.length === 0) return;
    if (sendMessage.isPending || pendingPairId) return;

    // Wait a beat so the user can see the previous response
    const timer = window.setTimeout(() => {
      const [nextPrompt, ...rest] = pendingRoutineSteps;
      setPendingRoutineSteps(rest);
      if (nextPrompt) {
        void handleSend(nextPrompt);
      }
      if (rest.length === 0) {
        onRoutineComplete?.();
      }
    }, 1500);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRoutineSteps, sendMessage.isPending, pendingPairId]);

  // Use initialMessages as immediate seed while the conversation query loads,
  // then switch to server data once available — eliminates the loading gap.
  const serverMessages = useMemo(() => {
    if (data?.messages && data.messages.length > 0) {
      return data.messages.map(toUiMessage);
    }
    if (initialMessages && initialMessages.length > 0) {
      return initialMessages.map(toUiMessage);
    }
    return [];
  }, [data?.messages, initialMessages]);

  // Clear initialMessages once the server query has loaded real data
  useEffect(() => {
    if (data?.messages && data.messages.length > 0 && initialMessages) {
      onInitialMessagesConsumed?.();
    }
  }, [data?.messages, initialMessages, onInitialMessagesConsumed]);

  // Build timeline: server messages + ONLY active local messages (pending/streaming/failed).
  // Local messages with status 'done' are NEVER rendered — the server query is authoritative.
  // This eliminates any race condition between local cleanup and query refetch.
  const timeline = useMemo(() => {
    const activeLocal = localMessages.filter(
      (m) => m.status === 'pending' || m.status === 'streaming' || m.status === 'failed',
    );
    return [...serverMessages, ...activeLocal].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
  }, [serverMessages, localMessages]);

  function updatePairStatus(pairId: string, updater: (message: ChatUiMessage) => ChatUiMessage) {
    setLocalMessages((prev) =>
      prev.map((message) => (message.pair_id === pairId ? updater(message) : message)),
    );
  }

  function streamAssistantResponse(pairId: string, streamMessageId: string, assistant: AiMessage) {
    const target = assistant.content;
    let cursor = 0;
    const chunk = Math.max(1, Math.ceil(target.length / 40));

    streamTimersRef.current.forEach((timer, key) => {
      if (key.startsWith(pairId)) {
        window.clearInterval(timer);
        streamTimersRef.current.delete(key);
      }
    });

    const timerKey = `${pairId}-assistant`;
    const timer = window.setInterval(() => {
      cursor = Math.min(target.length, cursor + chunk);

      if (cursor >= target.length) {
        // Streaming complete — remove local pair and fetch server data
        window.clearInterval(timer);
        streamTimersRef.current.delete(timerKey);
        setLocalMessages((prev) => prev.filter((m) => m.pair_id !== pairId));
        void queryClient.invalidateQueries({ queryKey: ['ai', 'conversation', conversationId] });
        return;
      }

      // Still streaming — update content progressively
      const content = target.slice(0, cursor);
      setLocalMessages((prev) =>
        prev.map((message) =>
          message.id === streamMessageId
            ? { ...message, content, status: 'streaming' as const }
            : message,
        ),
      );
    }, 25);

    streamTimersRef.current.set(timerKey, timer);
  }

  /**
   * Real SSE streaming path (AI SDK). Returns true when the send was fully
   * handled (success reflected in UI state) and false when the caller should
   * fall back to the classic request/response path. Throws on stream errors so
   * the caller's catch block applies the same failure UX as the legacy path.
   */
  async function sendViaStream(
    message: string,
    pairId: string,
    tempAssistantId: string,
  ): Promise<boolean> {
    const captured: { error: StreamError | null; result: StreamResult | null } = {
      error: null,
      result: null,
    };

    const outcome = await streamChatMessage(conversationId, message, {
      onToken: (content) => {
        setLocalMessages((prev) =>
          prev.map((m) =>
            m.id === tempAssistantId ? { ...m, content, status: 'streaming' as const } : m,
          ),
        );
      },
      onWidgets: (widgets) => {
        setLocalMessages((prev) =>
          prev.map((m) => (m.id === tempAssistantId ? { ...m, widgets } : m)),
        );
      },
      onDone: (result) => {
        captured.result = result;
      },
      onError: (error) => {
        captured.error = error;
      },
    });

    if (outcome.ok) {
      // The server persists the full message at stream end — the refetch is
      // authoritative (same reconciliation as streamAssistantResponse).
      setLocalMessages((prev) => prev.filter((m) => m.pair_id !== pairId));
      void queryClient.invalidateQueries({ queryKey: ['ai', 'conversation', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] });
      void queryClient.invalidateQueries({ queryKey: AI_CREDITS_STATUS_QUERY_KEY });
      if (captured.result?.toolNames.some((name) => PRODUCT_MUTATING_TOOLS.has(name))) {
        void queryClient.invalidateQueries({ queryKey: ['products'] });
      }
      return true;
    }

    if (outcome.errorCode === 'STREAMING_UNAVAILABLE') {
      streamingUnavailable = true;
      return false;
    }

    if (captured.error) {
      throw new ApiError(
        captured.error.message,
        captured.error.code ?? 'AI_API_ERROR',
        captured.error.status ?? 0,
      );
    }

    throw new Error(t('chat_error'));
  }

  async function handleSend(message: string, image?: File) {
    const pairId = makeId('pair');
    const now = new Date().toISOString();
    const tempUserId = makeId('tmp-user');
    const tempAssistantId = makeId('tmp-assistant');
    setPendingPairId(pairId);
    setThreadError(null);
    setLocalMessages((prev) => [
      ...prev,
      {
        id: tempUserId,
        role: 'user',
        content: image ? `${message}\n\n[Image jointe]` : message,
        created_at: now,
        status: 'pending',
        pair_id: pairId,
      },
      {
        id: tempAssistantId,
        role: 'assistant',
        content: '',
        created_at: now,
        status: 'streaming',
        pair_id: pairId,
      },
    ]);

    try {
      // Le endpoint streaming ne prend pas de fichier : une image jointe passe
      // par le POST multipart classique.
      if (!streamingUnavailable && !image) {
        const handledByStream = await sendViaStream(message, pairId, tempAssistantId);
        if (handledByStream) return;
      }

      const result = await sendMessage.mutateAsync(image ? { message, image } : message);

      // Mark user message as 'streaming' so it stays visible in timeline
      // until streamAssistantResponse completes and removes the entire pair.
      updatePairStatus(pairId, (item) =>
        item.role === 'user' ? { ...item, status: 'streaming' as const } : item,
      );

      streamAssistantResponse(pairId, tempAssistantId, result.assistant);
    } catch (mutationError) {
      const errorMessage = normalizeErrorMessage(mutationError, t);
      const errorCode = mutationError instanceof ApiError ? mutationError.code : null;
      setThreadError(errorMessage);
      setThreadErrorCode(errorCode);
      updatePairStatus(pairId, (item) => ({
        ...item,
        status: 'failed',
        error_message: errorMessage,
        error_code: errorCode,
      }));
    } finally {
      setPendingPairId(null);
    }
  }

  function retryMessage(message: ChatUiMessage) {
    if (!message.pair_id || sendMessage.isPending) return;
    const failedUser = localMessages.find(
      (entry) => entry.pair_id === message.pair_id && entry.role === 'user',
    );
    if (!failedUser) return;

    setLocalMessages((prev) => prev.filter((entry) => entry.pair_id !== message.pair_id));
    void handleSend(failedUser.content);
  }

  // ── Voice conversation: deferred logic (needs timeline + handleSend) ─────
  // Process any pending voice transcript that arrived while handleSend wasn't available.
  useEffect(() => {
    if (pendingSendRef.current && !sendMessage.isPending && !pendingPairId) {
      const text = pendingSendRef.current;
      pendingSendRef.current = null;
      void handleSend(text);
    }
  });

  // When a new assistant message finishes streaming in voice mode → TTS summary → re-listen.
  const lastTimelineAssistant = useMemo(() => {
    for (let i = timeline.length - 1; i >= 0; i--) {
      const msg = timeline[i];
      if (msg?.role === 'assistant' && msg.status === 'done' && msg.content) return msg;
    }
    return null;
  }, [timeline]);

  useEffect(() => {
    if (
      !voiceConversation ||
      !lastTimelineAssistant ||
      lastTimelineAssistant.id === lastSpokenMsgIdRef.current
    )
      return;

    lastSpokenMsgIdRef.current = lastTimelineAssistant.id;
    const summary = extractTtsSummary(lastTimelineAssistant.content);
    speak(summary);
  }, [voiceConversation, lastTimelineAssistant, speak]);

  // After TTS finishes speaking → re-activate mic.
  const wasSpeakingRef = useRef(false);
  useEffect(() => {
    if (isSpeaking) {
      wasSpeakingRef.current = true;
    } else if (wasSpeakingRef.current) {
      wasSpeakingRef.current = false;
      if (voiceConversationRef.current && !isListening) {
        startListening();
      }
    }
  }, [isSpeaking, isListening, startListening]);

  const title = data?.conversation.title ?? t('chat_new');
  // Suggestions belong to the welcome screen — inside an open conversation they
  // must not crowd out the messages. Only show them while the thread is empty.
  const showPromptChips = timeline.length === 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {!hideInternalHeader &&
        (isMobile ? (
          <ChatGlassHeader
            title={title}
            onBack={showBackButton ? onBack : undefined}
            onNewConversation={onNewConversation}
          />
        ) : (
          <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-200 dark:border-charcoal-700 bg-white dark:bg-card">
            {showBackButton && (
              <Button variant="ghost" size="icon" onClick={onBack} aria-label="Retour">
                <ArrowLeft className="size-4" />
              </Button>
            )}
            <span className="font-semibold text-sm text-charcoal-900 truncate">{title}</span>
            <div className="ml-auto">
              <VoiceConversationToggle
                enabled={voiceConversation}
                onToggle={handleVoiceToggle}
                isSupported={voiceSupported}
              />
            </div>
          </div>
        ))}

      {showPromptChips && (
        <ChatPromptChips
          onSelect={(prompt) => void handleSend(prompt)}
          mobileCollapsible
          defaultExpanded
        />
      )}

      <div className="px-3 pt-3 lg:hidden">
        <ChatQuotaBanner />
      </div>

      {threadError &&
        threadErrorCode !== 'INSUFFICIENT_CREDITS' &&
        threadErrorCode !== 'FEATURE_NOT_AVAILABLE' && (
          <div className="mx-3 mt-3 rounded-md border border-error bg-error-light p-2 text-xs text-error-dark">
            <p>{threadError}</p>
          </div>
        )}

      <AiCreditsPurchaseSheet open={purchaseOpen} onOpenChange={setPurchaseOpen} quick />

      <div className="relative min-h-0 flex-1">
        <div
          ref={messagesContainerRef}
          className="flex h-full flex-col gap-3 overflow-y-auto overscroll-contain scrollbar-none p-3"
          aria-live="polite"
          onScroll={handleScroll}
        >
          {isLoading && !initialMessages && (
            <p className="text-sm text-neutral-400 text-center py-4">{t('loading')}</p>
          )}

          {!isLoading && error && (
            <div className="mx-auto max-w-md rounded-md border border-error bg-error-light p-3 text-center text-sm text-error-dark">
              <p className="font-medium">Impossible de charger la conversation</p>
              <p className="mt-1 text-xs opacity-80">
                {error instanceof Error && error.message.includes('NetworkError')
                  ? 'Vérifiez votre connexion internet et réessayez.'
                  : 'Une erreur est survenue. Veuillez réessayer.'}
              </p>
            </div>
          )}

          {!isLoading && timeline.length === 0 && (
            <p className="text-sm text-neutral-500 text-center py-10">{t('chat_thread_empty')}</p>
          )}

          {creditsStatus &&
            !creditsStatus.unlimited &&
            creditsStatus.balance !== undefined &&
            creditsStatus.balance > 0 &&
            creditsStatus.balance <= 5 &&
            timeline.length > 0 && (
              <div className="mx-auto flex max-w-md items-center gap-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                <Coins className="size-3.5 shrink-0" />
                <span>
                  Plus que {creditsStatus.balance} crédit
                  {creditsStatus.balance !== 1 ? 's' : ''} restant
                  {creditsStatus.balance !== 1 ? 's' : ''}
                </span>
                <button
                  type="button"
                  onClick={() => setPurchaseOpen(true)}
                  className="ml-auto shrink-0 rounded-md border border-amber-400 dark:border-amber-600 bg-white dark:bg-amber-900/50 px-2 py-0.5 text-[11px] font-medium hover:bg-amber-100 dark:hover:bg-amber-900 transition-colors"
                >
                  Recharger
                </button>
              </div>
            )}

          {timeline.map((msg, idx) => {
            const isLastAssistant =
              msg.role === 'assistant' &&
              !timeline.slice(idx + 1).some((m) => m.role === 'assistant');
            const commonProps = {
              message: msg,
              conversationId,
              onRetry: retryMessage,
              onConfirm: (text: string) => void handleSend(text),
              onCancel: (text: string) => void handleSend(text),
              onFeedback: (messageId: string, feedback: 'positive' | 'negative') =>
                feedbackMutation.mutate({ messageId, feedback }),
              onSelectFollowUp: (prompt: string) => void handleSend(prompt),
              isLastAssistant,
            };
            return isMobile ? (
              <ChatMessageBubble key={msg.id} {...commonProps} variant={mobileVariant} />
            ) : (
              <ChatMessage key={msg.id} {...commonProps} />
            );
          })}

          {(threadErrorCode === 'INSUFFICIENT_CREDITS' ||
            threadErrorCode === 'FEATURE_NOT_AVAILABLE') &&
            creditsStatus?.balance !== undefined && (
              <ChatInlineRecharge
                onPurchaseComplete={() => {
                  setThreadError(null);
                  setThreadErrorCode(null);
                }}
              />
            )}

          {(threadErrorCode === 'INSUFFICIENT_CREDITS' ||
            threadErrorCode === 'FEATURE_NOT_AVAILABLE') &&
            creditsStatus?.balance === undefined && (
              <div className="mx-auto my-3 max-w-[340px] rounded-xl border border-error bg-error-light p-4 text-center text-sm text-error-dark">
                <p className="font-medium">Crédits épuisés</p>
                <p className="mt-1 text-xs">
                  Contactez le propriétaire de la boutique pour recharger les crédits.
                </p>
              </div>
            )}

          <div ref={bottomRef} />
        </div>

        {/* Scroll-to-bottom button */}
        {showScrollButton && (
          <button
            type="button"
            className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full bg-charcoal-900/90 dark:bg-charcoal-700/90 px-3 py-1.5 text-xs font-medium text-white shadow-soft-md backdrop-blur-sm flex items-center gap-1.5 transition-opacity"
            onClick={() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' })}
          >
            <ChevronDown className="size-3.5" />
            Messages récents
          </button>
        )}
      </div>

      <ChatInput
        onSend={(message, image) => void handleSend(message, image)}
        disabled={sendMessage.isPending || pendingPairId !== null}
        draftKey={conversationId}
        maxLength={8000}
        quickCommands={QUICK_COMMANDS}
        placeholder={pendingPairId ? t('chat_thinking') : t('chat_placeholder')}
        enableImageUpload
        mobileCompact
        mobileVariant={mobileVariant}
      />
    </div>
  );
}
